import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { submitApplication } from "../lib/api";
import Reveal from "../components/Reveal";

const TIDYCAL = "/book";

const schema = z.object({
  full_name: z.string().min(1, "Please share your name."),
  business_name: z.string().min(1, "What's the business called?"),
  email: z.string().email("Enter a valid email."),
  website_url: z.string().optional().or(z.literal("")),
  build_type: z.enum(["Website", "Business Tool", "Website + Tool", "Not Sure Yet"], {
    errorMap: () => ({ message: "Pick the closest option." }),
  }),
  business_description: z.string().min(10, "A sentence or two helps a lot."),
  biggest_problem: z.string().min(10, "Just be honest — a sentence is enough."),
  timeline: z.enum(["ASAP", "Within 1 month", "1–3 months", "Just exploring"], {
    errorMap: () => ({ message: "Pick the closest option." }),
  }),
  case_study_openness: z.enum(["Yes", "No", "Maybe — open to it"], {
    errorMap: () => ({ message: "Pick one." }),
  }),
  referral_source: z.string().min(1, "How'd you find us?"),
});

function SuccessState() {
  return (
    <Reveal className="text-center py-16" data-testid="apply-success">
      <div className="mx-auto mb-8 h-14 w-14 rounded-full border border-teal flex items-center justify-center">
        <svg className="checkmark-svg" width="28" height="28" viewBox="0 0 24 24" fill="none">
          <path d="M5 12l4.5 4.5L19 7" stroke="#00B4A6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <h2 className="display-h2 mb-5">Application received.</h2>
      <p className="text-lg text-ink/80 max-w-xl mx-auto mb-10">
        I'll review it personally and get back to you within 48 hours. Check your inbox.
      </p>
      <a href={TIDYCAL} target="_blank" rel="noopener noreferrer" className="cta-ghost" data-testid="apply-success-call-link">
        Or grab a discovery call slot now <span aria-hidden="true">→</span>
      </a>
    </Reveal>
  );
}

export default function Apply() {
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState("");
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      full_name: "",
      business_name: "",
      email: "",
      website_url: "",
      build_type: "",
      business_description: "",
      biggest_problem: "",
      timeline: "",
      case_study_openness: "",
      referral_source: "",
    },
    mode: "onTouched",
  });

  useEffect(() => {
    document.title = "Apply — Socialize Video";
    window.scrollTo(0, 0);
  }, []);

  const caseStudyValue = watch("case_study_openness");

  const onSubmit = async (values) => {
    setServerError("");
    try {
      await submitApplication({
        ...values,
        website_url: values.website_url || null,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setServerError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="bg-bg" data-testid="apply-page">
      <section className="pt-20 md:pt-28 pb-12 md:pb-16" data-testid="apply-header">
        <div className="container-x grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-9 lg:col-span-9">
            <div className="eyebrow mb-6">Apply</div>
            <h1 className="display-hero mb-7">Apply for a Build</h1>
            <p className="text-xl text-ink/80 max-w-2xl leading-relaxed">
              I review every application personally. If it's a fit, I'll reach out within 48 hours to schedule a discovery call.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-32 md:pb-40">
        <div className="container-x max-w-2xl">
          {submitted ? (
            <SuccessState />
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-7" noValidate data-testid="apply-form">
              {/* 1 */}
              <div>
                <label className="field-label" htmlFor="full_name">Your name</label>
                <input id="full_name" className="field-input" placeholder="Jane Doe" {...register("full_name")} data-testid="input-full-name" />
                {errors.full_name && <span className="field-error" data-testid="error-full-name">{errors.full_name.message}</span>}
              </div>

              {/* 2 */}
              <div>
                <label className="field-label" htmlFor="business_name">Business name</label>
                <input id="business_name" className="field-input" placeholder="Acme Co." {...register("business_name")} data-testid="input-business-name" />
                {errors.business_name && <span className="field-error" data-testid="error-business-name">{errors.business_name.message}</span>}
              </div>

              {/* email */}
              <div>
                <label className="field-label" htmlFor="email">Email</label>
                <input id="email" type="email" className="field-input" placeholder="you@business.com" {...register("email")} data-testid="input-email" />
                {errors.email && <span className="field-error" data-testid="error-email">{errors.email.message}</span>}
              </div>

              {/* 3 */}
              <div>
                <label className="field-label" htmlFor="website_url">
                  Website URL <span className="optional">if you have one — optional</span>
                </label>
                <input id="website_url" className="field-input" placeholder="https://yourcurrentsite.com" {...register("website_url")} data-testid="input-website-url" />
              </div>

              {/* 4 */}
              <div>
                <label className="field-label" htmlFor="build_type">What do you need built?</label>
                <select id="build_type" className="field-select" {...register("build_type")} data-testid="select-build-type">
                  <option value="">Choose one…</option>
                  <option>Website</option>
                  <option>Business Tool</option>
                  <option>Website + Tool</option>
                  <option>Not Sure Yet</option>
                </select>
                {errors.build_type && <span className="field-error" data-testid="error-build-type">{errors.build_type.message}</span>}
              </div>

              {/* 5 */}
              <div>
                <label className="field-label" htmlFor="business_description">Describe your business in 2–3 sentences</label>
                <textarea id="business_description" rows={4} className="field-textarea" placeholder="What you do, who you serve, what makes you different." {...register("business_description")} data-testid="input-business-description" />
                {errors.business_description && <span className="field-error" data-testid="error-business-description">{errors.business_description.message}</span>}
              </div>

              {/* 6 */}
              <div>
                <label className="field-label" htmlFor="biggest_problem">What's the biggest problem this build would solve for you?</label>
                <textarea id="biggest_problem" rows={3} className="field-textarea" placeholder="Be honest — direct answers help." {...register("biggest_problem")} data-testid="input-biggest-problem" />
                {errors.biggest_problem && <span className="field-error" data-testid="error-biggest-problem">{errors.biggest_problem.message}</span>}
              </div>

              {/* 7 */}
              <div>
                <label className="field-label" htmlFor="timeline">What's your timeline?</label>
                <select id="timeline" className="field-select" {...register("timeline")} data-testid="select-timeline">
                  <option value="">Choose one…</option>
                  <option>ASAP</option>
                  <option>Within 1 month</option>
                  <option>1–3 months</option>
                  <option>Just exploring</option>
                </select>
                {errors.timeline && <span className="field-error" data-testid="error-timeline">{errors.timeline.message}</span>}
              </div>

              {/* 8 */}
              <div>
                <span className="field-label">Are you open to being featured as a case study?</span>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {["Yes", "No", "Maybe — open to it"].map((opt) => (
                    <label
                      key={opt}
                      className={`radio-card ${caseStudyValue === opt ? "is-checked" : ""}`}
                      data-testid={`radio-case-study-${opt.split(" ")[0].toLowerCase()}`}
                    >
                      <input type="radio" value={opt} {...register("case_study_openness")} />
                      <span>{opt}</span>
                    </label>
                  ))}
                </div>
                {errors.case_study_openness && <span className="field-error" data-testid="error-case-study">{errors.case_study_openness.message}</span>}
              </div>

              {/* 9 */}
              <div>
                <label className="field-label" htmlFor="referral_source">How did you hear about Socialize Video?</label>
                <input id="referral_source" className="field-input" placeholder="Referral, search, podcast, etc." {...register("referral_source")} data-testid="input-referral-source" />
                {errors.referral_source && <span className="field-error" data-testid="error-referral-source">{errors.referral_source.message}</span>}
              </div>

              {serverError && (
                <p className="field-error text-base" data-testid="apply-server-error">{serverError}</p>
              )}

              <button type="submit" disabled={isSubmitting} className="cta w-full justify-center text-base py-4" data-testid="apply-submit">
                {isSubmitting ? "Submitting…" : "Submit My Application"} <span aria-hidden="true">→</span>
              </button>

              <p className="text-sm text-muted leading-relaxed pt-2" data-testid="apply-trust-note">
                Not everyone who applies gets a build — and that's by design. I work with businesses where I can make a real difference. If I don't think I'm the right fit, I'll tell you.
                <br /><br />
                No spam. No sales calls. Just a direct conversation.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
